import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Container, Item } from 'crehana-ui/Grid';
import Text from 'crehana-ui/Text';
import TextField from 'crehana-ui/TextField';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import { purple, grayDark } from 'crehana-ui/styles/colors';
import Check from 'crehana-icons/CheckNew';
import SearchIcon from 'crehana-icons/Search';

import { PAGE_TRANSLATION_KEY } from 'Jsx/CrehanaApp/Pages/CampaignLandingV2/i18n';

import { Triangle } from './common';
import useOnClickOutside from './useOnClickOutside';

const splitUp = (arr: Array<any>, n: number) => {
  const rest = arr.length % n; // how much to divide
  let restUsed = rest; // to keep track of the division over the elements
  const partLength = Math.floor(arr.length / n);
  const result: Array<Array<any>> = [];

  for (let i = 0; i < arr.length; i += partLength) {
    let end = partLength + i;
    let add = false;

    if (rest !== 0 && restUsed) {
      // should add one element for the division
      end += 1;
      restUsed -= 1; // we've used one division element now
      add = true;
    }

    result.push(arr.slice(i, end)); // part of the array

    if (add) {
      i += 1; // also increment i in the case we added an extra element for division
    }
  }

  return result;
};


type OptionType = {
  value: string;
  label: string;
};

type OptionDialogProps = {
  positionRef: React.RefObject<HTMLDivElement>;
  options: OptionType[];
  selected: OptionType[] | [];
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  onSelected(opt: OptionType[]): void;
  handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>, cb: () => void): void;
  onToggleOpen(): void;
};

const OptionDialog: React.FC<OptionDialogProps> = ({
  options,
  positionRef,
  onSelected,
  handleKeyDown,
  setOpenDialog,
  onToggleOpen,
  selected,
}) => {
  const optionDialogRef = React.useRef<HTMLDivElement>(null);
  const [noResults, setNoResults] = React.useState(false);
  const [optionsState, setOptionsState] = React.useState(options);
  const [selectedState, setSelectedState] = React.useState<OptionType[]>(
    selected,
  );
  const [left, setLeft] = React.useState(0);
  const [top, setTop] = React.useState(0);
  const isClickedOutside = useOnClickOutside(optionDialogRef);
  const { t } = useTranslation(PAGE_TRANSLATION_KEY);

  const filterSoftwares = () => {
    onSelected(selectedState);
    onToggleOpen();
  };

  const handleSearch = event => {
    const newOptions = optionsState.filter(option =>
      option.value.includes(event.target.value.toLowerCase()),
    );

    setNoResults(false);

    if (event.target.value === '') {
      setOptionsState(options);
    } else if (newOptions.length === 0) {
      setNoResults(true);
      setOptionsState(options);
    } else {
      setOptionsState(newOptions);
    }
  };

  const checkIfOptionIsSelected = (opt: OptionType) =>
    selectedState.filter(sel => sel.value === opt.value).length > 0;

  const onSelectState = (option: OptionType) => {
    if (checkIfOptionIsSelected(option)) {
      setSelectedState(selectedState.filter(sel => sel.value !== option.value));
    } else {
      setSelectedState([...selectedState, option]);
    }
  };

  const chunkOptions: Array<OptionType[]> = splitUp(optionsState, 4);

  const getPageOffset = () => ({
    x:
      window.pageXOffset !== undefined
        ? window.pageXOffset
        : (
            document.documentElement ||
            document.body.parentNode ||
            document.body
          ).scrollLeft,
    y:
      window.pageYOffset !== undefined
        ? window.pageYOffset
        : (
            document.documentElement ||
            document.body.parentNode ||
            document.body
          ).scrollTop,
  });

  React.useEffect(() => {
    if (positionRef && positionRef.current) {
      const rect = positionRef.current.getBoundingClientRect();
      const pageOffset = getPageOffset();
      const currenttop = pageOffset.y + rect.top;
      const currentleft = pageOffset.x + rect.left;

      if (currenttop !== top || currentleft !== left) {
        setTop(currenttop);
        setLeft(currentleft);
      }
    }
    if (isClickedOutside) {
      setOpenDialog(false);
    } else {
      setOpenDialog(true);
    }
  }, [isClickedOutside]);

  if (left === 0 || top === 0) {
    return null;
  }

  return (
    <div
      className="absolute w-full"
      css={`
        top: 42px;
        z-index: 99;
        left: -57vw;
        width: 80vw;
      `}
    >
      <Container>
        <Item
          className="p-40 bg-white shadow-dropdown"
          cols={{ xs: 4 }}
          isSubgrid
          position="1/1"
          ref={optionDialogRef}
        >
          <Triangle
            css={`
              right: 280px;
            `}
          />
          <Item
            cols={{ xs: 4 }}
            isSubgrid
            className={`${noResults ? 'mb-20' : 'mb-40'}`}
          >
            <Item cols={{ xs: 1 }} className="flex items-center">
              <SearchIcon size={18} color={grayDark} />
              <TextField
                hint={t('FILTER_SOFTWARE_PLACEHOLDER', 'Encuentra tu software')}
                chat
                css={`
                  width: 100%;
                  > div {
                    border: none;
                  }
                `}
                onChange={handleSearch}
                autoFocus
              />
            </Item>
            <Item startAt={{ xs: 4 }} cols={{ xs: 1 }}>
              <div className="flex w-full justify-end">
                <PrimaryButton
                  label={t('FILTER_SOFTWARE_APPLY', 'Aplicar')}
                  onClick={filterSoftwares}
                />
              </div>
            </Item>
          </Item>
          {noResults && (
            <Item cols={{ xs: 4 }} className="mb-20">
              <Text type="label" color="secondary-main">
                {t('NO_RESULTS')}
              </Text>
            </Item>
          )}
          {chunkOptions.map((opt, j) => (
            <Item key={opt[0].value} className="flex flex-col" cols={{ xs: 1 }}>
              <div className={`${chunkOptions.length > j + 1 ? 'mr-32' : ''}`}>
                {opt.map((o, i) => (
                  <div
                    key={o.value}
                    onClick={() => onSelectState(o)}
                    onKeyDown={e => handleKeyDown(e, () => onSelectState(o))}
                    className={`flex items-center justify-between cursor-pointer ${
                      opt.length > i + 1 ? 'mb-16' : ''
                    }`}
                  >
                    <Text
                      className={checkIfOptionIsSelected(o) ? 'font-bold' : ''}
                      type="body4"
                      color={
                        checkIfOptionIsSelected(o)
                          ? 'purple-main'
                          : 'base-main'
                      }
                    >
                      {o.label}
                    </Text>
                    {checkIfOptionIsSelected(o) && (
                      <Check size={16} color={purple} />
                    )}
                  </div>
                ))}
              </div>
            </Item>
          ))}
        </Item>
      </Container>
    </div>
  );
};

export default OptionDialog;
